import { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, X } from 'lucide-react';
import { ControlCreatePanel } from '../features/controls/components/ControlFormFields.tsx';
import { ControlBulkUpload } from '../features/controls/components/ControlBulkUpload.tsx';
import { TENANT_PATHS } from '../routing/paths.ts';

const MODE = {
  UPLOAD: 'upload',
  MANUAL: 'manual',
};

function NewControl() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo =
    typeof location.state?.returnTo === 'string' ? location.state.returnTo : TENANT_PATHS.SOX_CONTROLS;

  const [mode, setMode] = useState(MODE.UPLOAD);
  const [error, setError] = useState(null);

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
    setError(null);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(returnTo)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            type="button"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Create New Controls</h1>
            <p className="text-sm text-gray-500">
              Bulk upload from a spreadsheet or create a control manually.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
              <button
                type="button"
                onClick={() => handleModeChange(MODE.MANUAL)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === MODE.MANUAL ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Create Manually
              </button>
              <button
                type="button"
                onClick={() => handleModeChange(MODE.UPLOAD)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === MODE.UPLOAD ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Bulk Upload
              </button>
            </div>

            {error && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="ml-auto p-1 text-red-400 hover:text-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {mode === MODE.UPLOAD && <ControlBulkUpload returnTo={returnTo} />}

            {mode === MODE.MANUAL && (
              <ControlCreatePanel
                returnTo={returnTo}
                detailsHeaderSlot={(
                  <p className="text-sm text-gray-500">
                    Title and control wording (at least 20 characters) are required. The control is saved as a draft until required fields are set.
                  </p>
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewControl;
